import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { AuthConfig } from 'angular-oauth2-oidc';
import { BrandingService } from '../../../../branding/services/branding.service';
import { BrandConfig } from '../../../../branding/models/brand-config.model';
import { RoutesHelper } from 'src/app/core/helpers/routes.helper';
import { Router } from '@angular/router';
import { keycloakAuthConfig } from '../config/keycloak-auth.config';
import { Es01ClientGlobalStore } from 'src/app/clients/es01/store/es01-client-global.store';

export interface KeycloakDynamicConfig {
  realm: string;
  clientId: string;
}

const BASE_CLIENT_ID = 'console';

const realmNameToClientId = (realmName: string): string => {
  const parts = realmName.split('-');
  const environment = parts.length > 1 ? parts[parts.length - 1] : null;

  return environment ? `${BASE_CLIENT_ID}-${environment}` : BASE_CLIENT_ID;
};

@Injectable()
export class KeycloakDynamicConfigService {
  private configSubject = new BehaviorSubject<KeycloakDynamicConfig | null>(
    null
  );
  private configCache$: Observable<KeycloakDynamicConfig> | null = null;
  private readonly es01ClientStore = inject(Es01ClientGlobalStore);

  constructor(
    private readonly brandingService: BrandingService,
    private readonly router: Router
  ) {}

  /**
   * Fetch Keycloak configuration from branding service
   * This will typically be called with a brand code to get the specific realm and client
   */
  fetchKeycloakConfig(): Observable<KeycloakDynamicConfig> {
    if (this.configCache$) {
      console.log('🔄 Using cached Keycloak configuration');
      return this.configCache$;
    }

    // brandCode is resolved from the es-01 clients collection (full-host match,
    // populated in APP_INITIALIZER). `currentBrandCode()` already falls back to
    // the subdomain/env brandCode for non-es01 apps or when no row matches.
    const brandCode = this.es01ClientStore.currentBrandCode();
    console.log('🔍 Fetching Keycloak config for brand code:', brandCode);

    this.configCache$ = this.brandingService
      .getBrandByBrandCode(brandCode)
      .pipe(
        map((brandConfig: BrandConfig) => {
          console.log('✅ Successfully fetched brand config:', brandConfig);

          // Validate that required fields are present
          if (!brandConfig.realm) {
            const missingFields = [];
            if (!brandConfig.realm) missingFields.push('realm');

            console.error(
              '❌ Missing required Keycloak configuration fields:',
              missingFields
            );
            console.error('📊 Brand config received:', brandConfig);

            // Store error for post-bootstrap navigation
            sessionStorage.setItem(
              'keycloak-config-error',
              JSON.stringify({
                error: `Missing required Keycloak configuration: ${missingFields.join(', ')}`,
                timestamp: new Date().toISOString(),
                brandCode: RoutesHelper.getSubDomain(),
                missingFields: missingFields,
              })
            );

            // Try to navigate to error page (may not work during APP_INITIALIZER)
            setTimeout(() => {
              this.router.navigate(['/config-error']).catch(() => {
                // Navigation failed, probably because router isn't ready
                console.log(
                  'Router navigation failed, error stored in sessionStorage'
                );
              });
            }, 100);

            throw new Error(
              `Missing required Keycloak configuration: ${missingFields.join(', ')}`
            );
          }

          // Extract Keycloak configuration from brand config
          // Only realm and clientId come from branding service, domain is static from environment
          const keycloakConfig: KeycloakDynamicConfig = {
            realm: brandConfig.realm,
            clientId: realmNameToClientId(brandConfig.realm),
          };

          console.log('🔧 Extracted Keycloak config:', keycloakConfig);

          // Cache the config
          this.configSubject.next(keycloakConfig);

          return keycloakConfig;
        }),
        catchError(error => {
          console.error(
            '❌ Failed to fetch Keycloak configuration from branding service:',
            error
          );
          console.error('📡 Brand code used:', brandCode);
          console.error(
            '🔗 API endpoint:',
            `${environment.tenantApi}tenant/branding/brand-code/${brandCode}`
          );

          // Store error for post-bootstrap navigation
          sessionStorage.setItem(
            'keycloak-config-error',
            JSON.stringify({
              error:
                'Failed to fetch Keycloak configuration from branding service',
              timestamp: new Date().toISOString(),
              brandCode: brandCode,
              endpoint: `${environment.tenantApi}tenant/branding/brand-code/${brandCode}`,
              originalError: error.message || error,
            })
          );

          // Try to navigate to error page (may not work during APP_INITIALIZER)
          console.error('🔧 Attempting to redirect to public error page...');
          setTimeout(() => {
            this.router.navigate(['/config-error']).catch(() => {
              // Navigation failed, probably because router isn't ready
              console.log(
                'Router navigation failed, error stored in sessionStorage'
              );
            });
          }, 100);

          return throwError(error);
        }),
        shareReplay(1)
      );

    return this.configCache$;
  }

  /**
   * Create AuthConfig for angular-oauth2-oidc based on dynamic configuration
   * Uses static domain from environment + dynamic realm and clientId from branding service
   */
  createAuthConfig(dynamicConfig: KeycloakDynamicConfig): AuthConfig {
    // Use static domain from environment, append realm from dynamic config
    const baseDomain = environment.domain.split('/realms/')[0];
    const fullDomain = `${baseDomain}/realms/${dynamicConfig.realm}`;

    return keycloakAuthConfig(dynamicConfig.realm, dynamicConfig.clientId);
  }

  /**
   * Get current cached configuration
   */
  getCurrentConfig(): KeycloakDynamicConfig | null {
    return this.configSubject.value;
  }

  /**
   * Observable for configuration changes
   */
  getConfigObservable(): Observable<KeycloakDynamicConfig | null> {
    return this.configSubject.asObservable();
  }

  /**
   * Clear cache to force reload on next request
   */
  clearCache(): void {
    this.configCache$ = null;
    this.configSubject.next(null);
  }
}
